/**
 * bridge/menu_controller.js — Hotwire Native "menu" bridge component (web side).
 *
 * Stimulus identifier: `bridge--menu` (glob-derived from the bridge/ subdir).
 * Wire a container with data-controller="bridge--menu" and describe each menu
 * entry as a child element carrying data-bridge--menu-target="item" plus
 * data-bridge-* attributes. Inside a Hotwire Native shell this renders a real
 * native nav-bar UIMenu; selecting an item forwards the choice back as a
 * click() on the originating item element (so the web app keeps owning the
 * resulting navigation/behaviour). Outside a native shell BridgeComponent never
 * connect()s, so this is a graceful no-op on web and Android (which is
 * web-rendered for this component) — the items render as their normal HTML.
 *
 * Name invariant: `static component` ("menu") MUST equal the iOS
 * MenuComponent `class var name`.
 *
 * Per-item attributes (read via BridgeElement.bridgeAttribute, i.e. data-bridge-*):
 *   ios-image     SF Symbol name for the iOS menu-item icon (optional)
 *   android-image image name for Android (forwarded, unused on iOS) (optional)
 *   destructive   "true" renders the item in red (optional)
 * Item title comes from each item element's `title` (data-bridge-title) attribute.
 * Container attribute:
 *   color         "#RRGGBB" tint for the native menu button (optional)
 *
 * Adapted from Joe Masilotti's canonical menu_controller
 * (github.com/joemasilotti/bridge-components).
 */
import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

export default class extends BridgeComponent {
  static component = "menu";
  static targets = ["item"];

  connect() {
    super.connect();
    this.#addMenuButton();
  }

  disconnect() {
    super.disconnect();
    this.#removeMenuButton();
  }

  #addMenuButton() {
    const items = this.itemTargets.map((target) => {
      const element = new BridgeElement(target);
      return {
        title: element.title,
        iosImage: element.bridgeAttribute("ios-image"),
        androidImage: element.bridgeAttribute("android-image"),
        destructive: element.bridgeAttribute("destructive") === "true",
      };
    });

    const color = this.bridgeElement.bridgeAttribute("color");
    this.send("connect", { items, color }, (message) => {
      const item = this.itemTargets[message.data.index];
      if (item) new BridgeElement(item).click();
    });
  }

  #removeMenuButton() {
    this.send("disconnect");
  }
}
